import clsx from 'clsx'
import { forwardRef, useMemo } from 'react'
import { CommonSelectProps, SelectBase } from '../SelectBase'
import { SingleValue } from '../types'
import { useFloatingDropdown } from '../useFloatingDropdown'
import styles from './SelectSingle.module.scss'
import { useSelectSingle } from './useSelectSingle'

export interface SelectSingleProps extends CommonSelectProps {
  defaultValue?: SingleValue
  value?: SingleValue
  onChange?: (value: SingleValue) => void
}

export const SelectSingle = forwardRef<HTMLDivElement, SelectSingleProps>(
  (
    {
      placeholder,
      children,
      defaultValue,
      defaultIsOpen = false,
      value,
      onChange,
      onBlur,
      onFocus,
      disabled = false,
      size = 'md',
      dropdownMaxHeight,
      ...props
    },
    ref,
  ) => {
    const {
      refs,
      floatingStyles,
      context,
      isOpen,
      setIsOpen,
      elementsRef,
      labelsRef,
      getReferenceProps,
      getFloatingProps,
      getItemProps,
      activeIndex,
    } = useFloatingDropdown({
      defaultIsOpen,
      disabled,
      dropdownMaxHeight,
    })

    const { selectedIndex, handleSelect, selectedChildren } = useSelectSingle({
      defaultValue,
      value,
      onChange: (val) => {
        onChange?.(val)
        setIsOpen(false)
      },
      children,
    })

    const selectContext = useMemo(
      () => ({
        activeIndex,
        selectedIndexes: selectedIndex !== null ? [selectedIndex] : [],
        getItemProps,
        handleSelect,
        size,
      }),
      [activeIndex, selectedIndex, getItemProps, handleSelect, size],
    )
    return (
      <SelectBase
        ref={ref}
        disabled={disabled}
        size={size}
        isOpen={isOpen}
        refs={refs}
        floatingStyles={floatingStyles}
        context={context}
        children={children}
        canGrowVertically={false}
        selectedChildren={
          <div className={clsx('mui-select-single-selected-wrapper', styles.wrapper, styles[size])}>
            {selectedChildren?.icon}
            <div className={clsx('mui-select-single-selected-children', styles.children)}>
              {selectedChildren?.children ?? placeholder}
            </div>
          </div>
        }
        referenceProps={getReferenceProps({ onBlur, onFocus })}
        floatingProps={getFloatingProps()}
        elementsRef={elementsRef}
        labelsRef={labelsRef}
        selectContext={selectContext}
        {...props}
      />
    )
  },
)

SelectSingle.displayName = 'SelectSingle'
